import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdAddAPhoto,
  MdBadge,
  MdChevronLeft,
  MdChevronRight,
  MdMailOutline,
  MdPhone,
  MdPictureAsPdf,
  MdTableView,
  MdViewList,
  MdViewModule,
} from 'react-icons/md';
import admissionsAPI from '../../api/admissionsAPI';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { fetchWithHrToken } from '../../utils/hrApiHelpers';
import {
  clearSavedListFilters,
  readSavedFilter,
  writeSavedListFilters,
} from '../../utils/admissionsListFilters';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';
import '../../employeeAttendance-admissions.css';

const PAGE_SIZE = 40;
const FILTER_KEY = 'ug-student-list';

const CAMPUS_FALLBACK = [
  { value: 'prof_addow', label: 'Prof Addow Campus' },
  { value: 'prof_shahiid', label: 'Prof Shahiid Campus' },
  { value: 'prof_shurie', label: 'Prof Shurie Campus' },
];

function resolveImageUrl(row, baseURL) {
  const raw = row.image_url || row.image;
  if (!raw || raw === 'false') return null;
  const text = String(raw);
  if (text.startsWith('data:') || text.startsWith('http')) return text;
  if (text.length > 64 && !text.includes('/') && !text.includes(' ')) {
    return `data:image/jpeg;base64,${text}`;
  }
  if (text.includes('/web/image/')) return null;
  const base = (baseURL || '').replace(/\/$/, '');
  return `${base}${text.startsWith('/') ? text : `/${text}`}`;
}

function StudentAvatar({ row }) {
  const [broken, setBroken] = useState(false);
  const src = resolveImageUrl(row, admissionsAPI.baseURL);
  if (src && !broken) {
    return (
      <img
        className="ea-ug-card__img"
        src={src}
        alt=""
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <span className="ea-ug-card__photo-placeholder" aria-hidden>
      <MdAddAPhoto size={22} />
    </span>
  );
}

function downloadBase64(b64, filename, mimetype) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: mimetype });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function formatCount(n) {
  return Number(n || 0).toLocaleString();
}

export default function UgStudentListReportPage() {
  const navigate = useNavigate();
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();

  const [facultyId, setFacultyId] = useState(() => readSavedFilter(FILTER_KEY, 'facultyId', ''));
  const [departmentId, setDepartmentId] = useState(() =>
    readSavedFilter(FILTER_KEY, 'departmentId', '')
  );
  const [batchId, setBatchId] = useState(() => readSavedFilter(FILTER_KEY, 'batchId', ''));
  const [gender, setGender] = useState(() => readSavedFilter(FILTER_KEY, 'gender', ''));
  const [district, setDistrict] = useState(() => readSavedFilter(FILTER_KEY, 'district', ''));
  const [campus, setCampus] = useState(() => readSavedFilter(FILTER_KEY, 'campus', ''));
  const [activeState, setActiveState] = useState(() =>
    readSavedFilter(FILTER_KEY, 'activeState', 'active')
  );

  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [campuses, setCampuses] = useState(CAMPUS_FALLBACK);

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(() => readSavedFilter(FILTER_KEY, 'offset', 0));
  const [viewMode, setViewMode] = useState(() =>
    readSavedFilter(FILTER_KEY, 'viewMode', 'kanban')
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState(null);

  const filterParams = useMemo(
    () => ({
      faculty_id: facultyId ? Number(facultyId) : undefined,
      department_id: departmentId ? Number(departmentId) : undefined,
      batch_id: batchId ? Number(batchId) : undefined,
      gender: gender || undefined,
      district: district || undefined,
      campus: campus || undefined,
      active_state: activeState || 'active',
    }),
    [facultyId, departmentId, batchId, gender, district, campus, activeState]
  );

  const resetForm = () => {
    clearSavedListFilters(FILTER_KEY);
    setFacultyId('');
    setDepartmentId('');
    setBatchId('');
    setGender('');
    setDistrict('');
    setCampus('');
    setActiveState('active');
    setOffset(0);
    setViewMode('kanban');
    setError(null);
  };

  const loadLookups = useCallback(async () => {
    const campusRes = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getCampuses(token)
    );
    if (!campusRes.error) {
      const list = campusRes.data?.campuses || campusRes.data?.records || [];
      if (list.length) setCampuses(list);
    }
    const facRes = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getFaculties(token, { exclude_pg: true })
    );
    if (!facRes.error) {
      setFaculties(facRes.data?.faculties || facRes.data?.records || []);
    }
    const distRes = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getDistricts(token)
    );
    if (!distRes.error) {
      setDistricts(distRes.data?.districts || distRes.data?.records || []);
    }
  }, [ensureHrToken]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    writeSavedListFilters(FILTER_KEY, {
      facultyId,
      departmentId,
      batchId,
      gender,
      district,
      campus,
      activeState,
      offset,
      viewMode,
    });
  }, [
    facultyId,
    departmentId,
    batchId,
    gender,
    district,
    campus,
    activeState,
    offset,
    viewMode,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getDepartments(facultyId || undefined, token, { exclude_pg: true })
      );
      if (cancelled) return;
      if (!fetchError) {
        setDepartments(data?.departments || data?.records || []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ensureHrToken, facultyId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const extra = { exclude_pg: true };
      if (facultyId) extra.faculty_id = Number(facultyId);
      const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getBatches(departmentId || undefined, token, extra)
      );
      if (cancelled) return;
      if (!fetchError) {
        setBatches(data?.batches || data?.records || []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ensureHrToken, departmentId, facultyId]);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getUgStudentListReport(
        {
          ...filterParams,
          limit: PAGE_SIZE,
          offset,
          include_image: viewMode === 'kanban',
        },
        token
      )
    );
    setLoading(false);
    if (fetchError) {
      setError(fetchError);
      setRecords([]);
      setTotal(0);
      return;
    }
    setRecords(data?.records || []);
    setTotal(data?.student_count ?? data?.total ?? 0);
  }, [ensureHrToken, filterParams, offset, viewMode]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  const openStudentList = () => {
    setError(null);
    if (total === 0) {
      setError('No students found for the selected criteria.');
      return;
    }
    const params = new URLSearchParams();
    Object.entries(filterParams).forEach(([key, value]) => {
      if (value != null && value !== '') params.set(key, String(value));
    });
    const query = params.toString();
    navigate(
      `/bu-dashboard/admissions/ug-student-list/view${query ? `?${query}` : ''}`
    );
  };

  const downloadFile = async (kind) => {
    if (busy) return;
    setBusy(kind);
    setError(null);
    const method =
      kind === 'pdf'
        ? admissionsAPI.downloadUgStudentListPdf
        : admissionsAPI.downloadUgStudentListExcel;
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      method.call(admissionsAPI, filterParams, token)
    );
    setBusy('');
    if (fetchError) {
      setError(fetchError);
      return;
    }
    const b64 = kind === 'pdf' ? data?.pdf_base64 : data?.excel_base64;
    if (!b64) {
      setError(kind === 'pdf' ? 'PDF was empty.' : 'Excel file was empty.');
      return;
    }
    downloadBase64(
      b64,
      data?.filename || (kind === 'pdf' ? 'Student_List.pdf' : 'Student_List.xlsx'),
      data?.mimetype ||
        (kind === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    );
  };

  return (
    <div className="ea-odoo-form-page ea-adm-form ea-ug-report">
      <div className="ea-odoo-form-bar ea-adm-cp">
        <div className="ea-adm-cp__left">
          <button type="button" className="ea-odoo-btn ea-adm-cp__new" onClick={resetForm}>
            New
          </button>
          <span className="ea-adm-cp__record">UG student list report</span>
        </div>
        <div className="ea-adm-cp__right">
          <div className="ea-odoo-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={viewMode === 'kanban' ? 'active' : ''}
              onClick={() => setViewMode('kanban')}
              title="Kanban"
            >
              <MdViewModule size={18} />
            </button>
            <button
              type="button"
              className={viewMode === 'list' ? 'active' : ''}
              onClick={openStudentList}
              title="List"
            >
              <MdViewList size={18} />
            </button>
          </div>
          <div className="ea-odoo-pager" aria-label="Pagination">
            <span>{loading ? '…' : `${rangeStart}-${rangeEnd} / ${formatCount(total)}`}</span>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canPrev || loading}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              aria-label="Previous page"
            >
              <MdChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canNext || loading}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              aria-label="Next page"
            >
              <MdChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      <form className="ea-odoo-sheet" onSubmit={(e) => e.preventDefault()}>
        <div className="ea-odoo-sheet__body">
          <div className="ea-odoo-cols">
            <div className="ea-odoo-group">
              <h3 className="ea-odoo-group__title">Academic Information</h3>
              <div className="ea-odoo-group__body">
                <label className="ea-odoo-field">
                  <span className="ea-odoo-field__label">Faculty</span>
                  <span className="ea-odoo-field__value">
                    <select
                      value={facultyId}
                      onChange={(e) => {
                        setFacultyId(e.target.value);
                        setDepartmentId('');
                        setBatchId('');
                        setOffset(0);
                      }}
                    >
                      <option value="" />
                      {faculties.map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.name}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
                <label className="ea-odoo-field">
                  <span className="ea-odoo-field__label">Department</span>
                  <span className="ea-odoo-field__value">
                    <select
                      value={departmentId}
                      onChange={(e) => {
                        setDepartmentId(e.target.value);
                        setBatchId('');
                        setOffset(0);
                      }}
                    >
                      <option value="" />
                      {departments.map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.name}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
              </div>
            </div>

            <div className="ea-odoo-group">
              <h3 className="ea-odoo-group__title">Batch Information</h3>
              <div className="ea-odoo-group__body">
                <label className="ea-odoo-field">
                  <span className="ea-odoo-field__label">Batch</span>
                  <span className="ea-odoo-field__value">
                    <select
                      value={batchId}
                      onChange={(e) => {
                        setBatchId(e.target.value);
                        setOffset(0);
                      }}
                    >
                      <option value="" />
                      {batches.map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.name}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
                <div className="ea-odoo-field">
                  <span className="ea-odoo-field__label">Total Students</span>
                  <span className="ea-odoo-field__value ea-ug-report__count">
                    {loading ? '…' : formatCount(total)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="ea-odoo-group">
            <h3 className="ea-odoo-group__title">Additional Filters</h3>
            <div className="ea-odoo-cols">
              <div className="ea-odoo-group__body">
                <label className="ea-odoo-field">
                  <span className="ea-odoo-field__label">Gender</span>
                  <span className="ea-odoo-field__value">
                    <select
                      value={gender}
                      onChange={(e) => {
                        setGender(e.target.value);
                        setOffset(0);
                      }}
                    >
                      <option value="" />
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </span>
                </label>
                <label className="ea-odoo-field">
                  <span className="ea-odoo-field__label">Student Status</span>
                  <span className="ea-odoo-field__value">
                    <select
                      value={activeState}
                      onChange={(e) => {
                        setActiveState(e.target.value);
                        setOffset(0);
                      }}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </span>
                </label>
              </div>
              <div className="ea-odoo-group__body">
                <label className="ea-odoo-field">
                  <span className="ea-odoo-field__label">District</span>
                  <span className="ea-odoo-field__value">
                    <select
                      value={district}
                      onChange={(e) => {
                        setDistrict(e.target.value);
                        setOffset(0);
                      }}
                    >
                      <option value="" />
                      {districts.map((row) => (
                        <option key={row.value || row} value={row.value || row}>
                          {row.label || row.name || row}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
                <label className="ea-odoo-field">
                  <span className="ea-odoo-field__label">Campus</span>
                  <span className="ea-odoo-field__value">
                    <select
                      value={campus}
                      onChange={(e) => {
                        setCampus(e.target.value);
                        setOffset(0);
                      }}
                    >
                      <option value="" />
                      {campuses.map((row) => (
                        <option key={row.value || row.id} value={row.value || row.id}>
                          {row.label || row.name}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {error ? <p className="ea-ug-report__error">{error}</p> : null}

          <div className="ea-ug-report__preview-head">
            {total > 0 ? (
              <div className="ea-odoo-tabs">
                <button
                  type="button"
                  className={`ea-odoo-tabs__btn${viewMode === 'kanban' ? ' active' : ''}`}
                  onClick={() => setViewMode('kanban')}
                >
                  Student List Preview (Box)
                </button>
              </div>
            ) : (
              <div />
            )}
            <div className="ea-ug-report__actions">
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--primary ea-odoo-btn--sm"
                onClick={openStudentList}
                disabled={busy || loading}
              >
                <MdViewList size={14} aria-hidden />
                View Students
              </button>
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--primary ea-odoo-btn--sm"
                onClick={() => downloadFile('pdf')}
                disabled={busy || loading}
              >
                <MdPictureAsPdf size={14} aria-hidden />
                {busy === 'pdf' ? 'Preparing…' : 'Download PDF'}
              </button>
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--success ea-odoo-btn--sm"
                onClick={() => downloadFile('excel')}
                disabled={busy || loading}
              >
                <MdTableView size={14} aria-hidden />
                {busy === 'excel' ? 'Preparing…' : 'Download Excel'}
              </button>
            </div>
          </div>

          {total === 0 && !loading ? null : (
            <div className="ea-odoo-kanban ea-ug-report__kanban">
              {loading && records.length === 0 ? (
                <p className="ea-hr-table__empty">Loading students…</p>
              ) : (
                records.map((row) => (
                  <article key={row.id} className="ea-ug-card">
                    <button
                      type="button"
                      className="ea-ug-card__main"
                      onClick={() =>
                        navigate(`/bu-dashboard/admissions/ug-student-list/${row.id}`)
                      }
                      aria-label={`Open ${row.name}`}
                    >
                      <div className="ea-ug-card__avatar">
                        <StudentAvatar row={row} />
                      </div>
                      <div className="ea-ug-card__body">
                        <h3 className="ea-ug-card__name">{row.name}</h3>
                        <p className="ea-ug-card__line">
                          <MdBadge size={14} aria-hidden />
                          <span>{row.student_no || 'N/A'}</span>
                        </p>
                        <p className="ea-ug-card__line">
                          <MdPhone size={14} aria-hidden />
                          <span>{row.phone || row.mobile || 'N/A'}</span>
                        </p>
                        <p className="ea-ug-card__line">
                          <MdMailOutline size={14} aria-hidden />
                          <span>{row.email || 'N/A'}</span>
                        </p>
                      </div>
                    </button>
                  </article>
                ))
              )}
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
